// src/context/AuthSecurityContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL_SECURITY } from "../utils/config";

export const AuthSecurityContext = createContext();

export const useAuthSecurity = () => useContext(AuthSecurityContext);

const SESSION_EXPIRED_TOAST = "security-session-expired";

// El token del módulo de seguridad vence a las 8 h (GeoModuleSecurity, LoginController).
// Antes la sesión seguía «abierta» en el front con el token vencido y cada pantalla
// fallaba con «Error al obtener empresas»; ahora se cierra y se vuelve al login.
const tokenExpiresAt = (token) => {
    try {
        const payload = JSON.parse(
            atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        );
        return typeof payload.exp === "number" ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
};

const isExpired = (token) => {
    const expiresAt = tokenExpiresAt(token);
    return expiresAt !== null && expiresAt <= Date.now();
};

const clearStoredSession = () => {
    localStorage.removeItem("SecurityToken");
    localStorage.removeItem("securityUserData");
};

export const AuthSecurityProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userData, setUserData] = useState(null);
    const [token, setToken] = useState(null);

    useEffect(() => {
        const storedToken = localStorage.getItem("SecurityToken");
        let storedUserData = localStorage.getItem("securityUserData");
        if (storedToken && isExpired(storedToken)) {
            clearStoredSession();
            return;
        }
        if (storedToken && storedUserData) {
            try {
                storedUserData = JSON.parse(storedUserData);
                setIsAuthenticated(true);
                setUserData(storedUserData);
                setToken(storedToken);
            } catch (error) {
                console.error("Error parsing securityUserData from localStorage:", error);
                localStorage.removeItem("securityUserData");
                setIsAuthenticated(false);
                setUserData(null);
            }
        }
    }, []);

    const login = ({ token, user }) => {
        setIsAuthenticated(true);
        setUserData(user);
        setToken(token);
        localStorage.setItem("SecurityToken", token);
        localStorage.setItem("securityUserData", JSON.stringify(user));
    };

    const logout = useCallback(() => {
        setIsAuthenticated(false);
        setUserData(null);
        setToken(null);
        clearStoredSession();
    }, []);

    const expireSession = useCallback(() => {
        logout();
        toast.info("Tu sesión de administración venció. Vuelve a iniciar sesión.", {
            toastId: SESSION_EXPIRED_TOAST,
        });
    }, [logout]);

    // Cierra la sesión justo cuando vence el token, aunque la pestaña siga abierta.
    useEffect(() => {
        if (!token) return undefined;
        const expiresAt = tokenExpiresAt(token);
        if (expiresAt === null) return undefined;
        const timer = setTimeout(expireSession, Math.max(0, expiresAt - Date.now()));
        return () => clearTimeout(timer);
    }, [token, expireSession]);

    // Si el módulo de seguridad rechaza el token (vencido, secreto rotado…), también.
    useEffect(() => {
        if (!token) return undefined;
        const interceptor = axios.interceptors.response.use(undefined, (error) => {
            const url = String(error?.config?.url || "");
            const authorization = String(error?.config?.headers?.Authorization || "");
            if (
                error?.response?.status === 401 &&
                url.startsWith(API_BASE_URL_SECURITY) &&
                authorization === `Bearer ${token}`
            ) {
                expireSession();
            }
            return Promise.reject(error);
        });
        return () => axios.interceptors.response.eject(interceptor);
    }, [token, expireSession]);

    return (
        <AuthSecurityContext.Provider
            value={{ isAuthenticated, userData, user: userData, login, logout }}
        >
            {children}
        </AuthSecurityContext.Provider>
    );
};
